// import api's and mutations
import { queries, mutations } from 'RestAPIs/rapini';

// import components
import { Box } from '@mui/material';

import LoadingOverlay from 'components/shared/LoadingOverlay';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { RecoilRoot } from 'recoil';
import { useColumnDefinitions } from './useColumnDefinitions';
import { useQueryClient } from 'react-query';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// import types
import { PlanUnitMix } from 'components/types';
import {
  DayLimitViolationsProps,
  ExtendedEmploymentDayViolationResponse
} from './DayLimitViolations.types';
import { EmploymentDayViolationResponse } from 'generated/index';

// import utils
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import utc from 'dayjs/plugin/utc';
import { Header } from './Header';
import showError from 'components/shared/showError';
import { showSuccessSnackbar } from 'components/shared/Snackbar';

import { useShallow } from 'zustand/shallow';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { StyledTableCRUDLayout } from './DayLimitViolations.styles';

dayjs.extend(utc);

const formatDateByPeriod = (
  value: Date | Date[] | null,
  period: 'month' | 'week',
  position: 'start' | 'end'
) => {
  const dateValue = Array.isArray(value)
    ? position === 'start'
      ? value[0]
      : value[1]
    : value;
  return dayjs(dateValue)[`${position}Of`](period).format('YYYY-MM-DD');
};

const DayLimitViolations = ({
  page = 'START',
  showEmployeeName = false
}: DayLimitViolationsProps) => {
  const columns = useColumnDefinitions({ showEmployeeName });
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const queryClient = useQueryClient();
  const {
    selectedDate: storeDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);

  const { period, setPeriod } = useDateNavigationStore(
    useShallow((state) => state)
  );

  const isCustomDateRange = period === 'custom';
  const isMonthRange = period === 'month';

  const startDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[0]).format('YYYY-MM-DD')
    : formatDateByPeriod(storeDate, isMonthRange ? 'month' : 'week', 'start');
  const endDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[1]).format('YYYY-MM-DD')
    : formatDateByPeriod(storeDate, isMonthRange ? 'month' : 'week', 'end');

  const organizationalUnitIds =
    selectedPlanUnits &&
    Array.from(
      // create an unique set of costCenter ids
      new Set(
        selectedPlanUnits
          .map((unit) =>
            'costCenters' in unit &&
            Array.isArray(unit.costCenters) &&
            unit.costCenters?.length > 0
              ? unit.costCenters.map(({ id }) => id)
              : unit.id
          )
          .flat()
      )
    ).join(',');

  const isOverview = !page || page === 'START';
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedRows, setSelectedRows] = useState<
    ExtendedEmploymentDayViolationResponse[]
  >([]);

  const getDayLimitViolationsByOrganizationalUnits = () =>
    queries.useFindDayLimitViolationsByOrganizationalUnits(
      startDate,
      endDate,
      // @ts-ignore
      organizationalUnitIds as string[],
      {
        enabled:
          shouldFetch &&
          !!organizationalUnitIds &&
          dayjs(startDate).isValid() &&
          dayjs(endDate).isValid()
      }
    );

  const { data = [], isLoading } = getDayLimitViolationsByOrganizationalUnits();

  const createPayOutMutation = mutations.useCreateDayLimitViolationPayOut({
    onSuccess: () => {
      showSuccessSnackbar();
      setSelectedRows([]);
      queryClient.invalidateQueries([
        'findDayLimitViolationsByOrganizationalUnits',
        startDate,
        endDate,
        organizationalUnitIds
      ]);
    },
    onError: (error) => {
      showError(error as any);
    }
  });

  const handlePayOut = () => {
    const payload = {
      requests: selectedRows.map((row) => ({
        employmentId: row.employment.id,
        startPeriod: row.periodStart!,
        payOutDate: row.periodEnd!,
        numberOfDaysPaidOut: row.numberOfViolationDays
      }))
    };
    createPayOutMutation.mutate(payload);
  };

  const rows = useMemo(
    () =>
      data.map((response: EmploymentDayViolationResponse) => ({
        ...response,
        disableSelection: !response.canBePaidOut,
        uniqueId: uuidv4()
      })),
    [data]
  );

  useEffect(() => {
    if (selectedPlanUnits.length > 0) setShouldFetch(true);
  }, [selectedPlanUnits, startDate, endDate]);

  useEffect(() => {
    if (isCustomDateRange) {
      setPeriod('custom');
    }
  }, [isCustomDateRange]);

  return (
    <RecoilRoot>
      <Header
        onSelectPlanUnit={setSelectedPlanUnits}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedDate={setSelectedDate}
        period={isCustomDateRange ? 'custom' : 'month'}
        selectedDateRange={selectedDateRange}
        selectedDate={dayjs(startDate).toDate()}
        showActions={isOverview}
        showPlanUnitSelector={isOverview}
        isTravelMovements
        dateNavigationOptions={['week', 'month', 'custom']}
        selection={selectedRows}
        onAction={handlePayOut}
      />
      <Box>
        <StyledTableCRUDLayout
          rows={rows}
          getRowId={(row) => row?.uniqueId}
          columns={columns}
          loading={isLoading}
          pageSize={50}
          dynamicHeight={true}
          editable={false}
          hideFooter={rows?.length === 0}
          actionsThreshold={4}
          pageSizeOptions={[5]}
          checkboxSelection={rows.some(
            ({ disableSelection }) => !disableSelection
          )}
          isRowSelectable={(params) => !params.row?.disableSelection}
          customSelectionModel={{
            type: 'include',
            ids: new Set<GridRowId>(
              selectedRows.map(({ uniqueId }) => uniqueId)
            )
          }}
          onSelectionChange={(selectionModel: GridRowSelectionModel) => {
            const selectedRowsData = rows.filter((row) =>
              selectionModel.ids.has(row.uniqueId)
            );
            setSelectedRows(selectedRowsData);
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'periodStart', sort: 'asc' }]
            }
          }}
        />
      </Box>
      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default DayLimitViolations;
